/**
 * O vocabulário da tela de Meta Ads — e o contrato dela com a documentação.
 *
 * Mesma ideia de `dashboard/analytics-ui.ts`: cada número que aparece na tela tem uma
 * entrada aqui, e cada entrada declara ONDE ele está definido. A diferença é que a Fase 8
 * traz três origens diferentes de número, e fingir que são a mesma coisa seria o começo do
 * problema — por isso `fonte` é uma união discriminada, e o typecheck não deixa criar
 * métrica sem dizer de qual das três ela é:
 *
 *  - `metricas`  — definida em `docs/product/metricas.md`. É o caso do gasto, que aparece
 *                  lá como `gasto(anúncio,T)`, no numerador do CPE (§3).
 *  - `espelho`   — número que a **Meta** informa e que nós copiamos sem transformar
 *                  (`spend`, `impressions`, `clicks`, `reach`). A definição dele é da Meta,
 *                  não nossa: `packages/db/src/schema/ads.ts` diz, no cabeçalho, que estas
 *                  tabelas são espelho do que a Meta diz, nunca número nosso.
 *  - `razao`     — razão calculada pelo NOSSO servidor (`ads.service.ts`): `cpc`, `cpm` e
 *                  `ctr`. **Estas três ainda não têm entrada em `metricas.md`**, e isso
 *                  está anotado em `pendenteEm` campo a campo, com a fórmula exata que o
 *                  servidor usa. Não foi inventada definição nenhuma aqui: o que está
 *                  escrito é o que `razao()` faz, copiado de lá.
 *
 * Nada neste arquivo calcula métrica (invariante 3). O que existe é texto, classificação e
 * duas conversões de UNIDADE (centavos → reais, dia → rótulo), que são apresentação.
 */
import { CircleCheck, Hourglass, KeyRound, Pause, TriangleAlert, Unplug } from 'lucide-react';
import type { BadgeTone } from '@mgdlead/ui';

import type { AdsConnectionStatus, AdsEntity, AdsLevel } from '../../lib/api';

/* ========================================================================== */
/*  De onde vem cada número                                                    */
/* ========================================================================== */

export type FonteDoNumero =
  /** Definido em `docs/product/metricas.md`. */
  | { tipo: 'metricas'; secao: string }
  /** Informado pela Meta e copiado sem transformação. A definição é dela. */
  | { tipo: 'espelho'; campoMeta: string }
  /** Razão calculada pelo nosso servidor — com a fórmula e a lacuna de documentação. */
  | { tipo: 'razao'; formula: string; pendenteEm: string };

export interface MetricaAdsSpec {
  label: string;
  fonte: FonteDoNumero;
  /** O que a tela diz quando o valor é `null`. Nunca "0", nunca travessão mudo. */
  semDados: string;
  /** Uma linha de explicação para quem passa o mouse no rótulo. */
  ajuda: string;
}

/**
 * **"Cliques" aqui NÃO é "Cliques" do resto do painel.**
 *
 * `metricas.md §3` define `cliques(T) = count(redirect_clicks)` — o clique no NOSSO
 * redirecionador. Este é o clique que a **Meta** contou no anúncio dela, e os dois números
 * nunca vão bater (bloqueador, desistência no meio do caminho, contagem própria da Meta).
 * Dois números diferentes com o mesmo rótulo na mesma casa é como se descobre, meses
 * depois, que a taxa de entrada estava sendo calculada contra o denominador errado — por
 * isso o rótulo aqui diz "no anúncio", e diz sempre.
 */
export const METRICA_ADS = {
  gasto: {
    label: 'Gasto',
    fonte: { tipo: 'metricas', secao: 'metricas.md §3 — gasto(anúncio,T), no numerador do CPE' },
    semDados: 'Nenhum dia deste período foi sincronizado ainda.',
    ajuda:
      'O que a Meta cobrou no período, na moeda da conta de anúncio. O dia é o da conta, não o do seu workspace.',
  },
  impressoes: {
    label: 'Impressões',
    fonte: { tipo: 'espelho', campoMeta: 'insights.impressions' },
    semDados: 'Nenhum dia deste período foi sincronizado ainda.',
    ajuda: 'Quantas vezes o anúncio foi exibido. Número da Meta, copiado como ela informa.',
  },
  cliques: {
    label: 'Cliques no anúncio',
    fonte: { tipo: 'espelho', campoMeta: 'insights.clicks' },
    semDados: 'Nenhum dia deste período foi sincronizado ainda.',
    ajuda:
      'Cliques que a META contou no anúncio. É outro número do que os cliques do seu redirecionador — os dois nunca batem, e somá-los não faz sentido.',
  },
  alcance: {
    label: 'Alcance',
    fonte: { tipo: 'espelho', campoMeta: 'insights.reach' },
    semDados: 'A Meta não informou alcance neste recorte. Isso é diferente de alcance zero.',
    ajuda: 'Pessoas distintas alcançadas. A Meta não informa alcance em todo recorte.',
  },
  cpc: {
    label: 'Custo por clique',
    fonte: {
      tipo: 'razao',
      formula: 'gasto ÷ cliques no anúncio (ads.service.ts)',
      pendenteEm: 'metricas.md §3 — ainda sem entrada própria',
    },
    semDados: 'Não houve clique no período — sem denominador, não há custo por clique.',
    ajuda:
      'Gasto dividido pelos cliques que a Meta contou. Calculado no servidor sobre os TOTAIS do período, nunca como média dos custos diários.',
  },
  cpm: {
    label: 'Custo por mil impressões',
    fonte: {
      tipo: 'razao',
      formula: 'gasto ÷ impressões × 1000 (ads.service.ts)',
      pendenteEm: 'metricas.md §3 — ainda sem entrada própria',
    },
    semDados: 'Não houve impressão no período — sem denominador, não há CPM.',
    ajuda: 'Quanto custa alcançar mil exibições. Calculado no servidor sobre os totais.',
  },
  ctr: {
    label: 'Taxa de clique',
    fonte: {
      tipo: 'razao',
      formula: 'cliques no anúncio ÷ impressões (ads.service.ts)',
      pendenteEm: 'metricas.md §3 — ainda sem entrada própria',
    },
    semDados: 'Não houve impressão no período — sem denominador, não há taxa.',
    ajuda: 'De cada cem exibições, quantas viraram clique dentro da Meta.',
  },
} as const satisfies Record<string, MetricaAdsSpec>;

export type MetricaAdsId = keyof typeof METRICA_ADS;

/* ========================================================================== */
/*  Número provisório × número definitivo                                      */
/* ========================================================================== */

/**
 * A regra, em uma frase: **dia com menos de quatro dias ainda muda.**
 *
 * A Meta reprocessa o número por até três dias depois do fechamento do dia (conversão
 * atrasada, modelagem, retentativa de pixel). Quem carimba isso é o servidor, na coluna
 * `is_final` — a tela **nunca recalcula**: `isFinalDay()` mora em `packages/db/ads-sync.ts`
 * justamente para o carimbo acontecer uma vez, por quem tem o relógio, em vez de duas telas
 * refazendo a conta com dois "hoje" diferentes.
 *
 * Os dois estados têm ÍCONE e PALAVRA diferentes, não só cor diferente: "ainda muda" e
 * "fechado" precisam ser distinguíveis num print em preto e branco e por quem não separa
 * âmbar de cinza (DESIGN_SYSTEM §1.3).
 */
export interface SituacaoDoDiaSpec {
  label: string;
  icon: typeof Hourglass;
  tone: BadgeTone;
  hint: string;
}

export const SITUACAO_DO_DIA: Record<'preliminar' | 'final', SituacaoDoDiaSpec> = {
  preliminar: {
    label: 'Provisório',
    icon: Hourglass,
    tone: 'warning',
    hint: 'Hoje ainda está acontecendo, e a Meta ajusta os números por até 3 dias depois. Este valor pode mudar.',
  },
  final: {
    label: 'Definitivo',
    icon: CircleCheck,
    tone: 'neutral',
    hint: 'Já passou o prazo em que a Meta ajusta os números: este valor não muda mais.',
  },
};

/**
 * Qual das duas situações. **Recebe o carimbo do servidor** (`row.isFinal`) — a tela não
 * compara datas para decidir isso: dois lugares decidindo com dois "hoje" diferentes é
 * exatamente o que a coluna `is_final` existe para impedir.
 */
export function situacaoDoDia(isFinal: boolean): SituacaoDoDiaSpec {
  return isFinal ? SITUACAO_DO_DIA.final : SITUACAO_DO_DIA.preliminar;
}

/** O aviso do topo quando algum dia do período ainda pode mudar (`totals.hasPreliminary`). */
export const AVISO_PRELIMINAR =
  'Parte deste período ainda pode mudar. A Meta continua ajustando o gasto, os cliques e as impressões por até 3 dias depois de cada dia (hoje, inclusive, ainda nem acabou). Linhas “Provisório” podem mudar; “Definitivo” não muda mais. Os totais abaixo incluem os provisórios.';

/* ========================================================================== */
/*  Estado da conexão                                                          */
/* ========================================================================== */

export interface StatusConexaoSpec {
  label: string;
  tone: BadgeTone;
  icon: typeof KeyRound;
  /** O que está acontecendo, em uma frase. */
  explica: string;
  /** O que a pessoa faz a respeito. `null` = não há nada a fazer. */
  acao: string | null;
  /** `true` quando o sync parou e os números na tela são os últimos que chegaram. */
  numerosParados: boolean;
}

/**
 * Os quatro estados, com frase de gente — o enum é como a TABELA chama isso.
 *
 * `TOKEN_EXPIRED` é o estado que esta tela existe para contar direito: a conexão sai da
 * fila do sync, um incidente `META_TOKEN_EXPIRED` abre, **e a leitura continua funcionando**
 * com o que já está no banco. Não é tela de erro — é número velho com aviso. Número velho
 * SEM aviso é o que faz alguém decidir errado.
 */
export const STATUS_CONEXAO: Record<AdsConnectionStatus, StatusConexaoSpec> = {
  ACTIVE: {
    label: 'Sincronizando',
    tone: 'success',
    icon: CircleCheck,
    explica: 'A conta está na fila do sincronizador e os números chegam sozinhos.',
    acao: null,
    numerosParados: false,
  },
  TOKEN_EXPIRED: {
    label: 'Token vencido',
    tone: 'warning',
    icon: KeyRound,
    explica:
      'O token desta conta parou de ser aceito pela Meta, então a conta saiu da fila do sincronizador. Os números abaixo continuam sendo os seus — eles só pararam no último dia que deu certo.',
    acao: 'Gere um token novo para o Usuário do Sistema no Business Manager e use "Colar um token novo" aqui. A sincronização recomeça sozinha, com o histórico intacto.',
    numerosParados: true,
  },
  ERROR: {
    label: 'Erro na sincronização',
    tone: 'danger',
    icon: TriangleAlert,
    explica:
      'A última rodada do sincronizador não terminou. Os números abaixo são os da última rodada que deu certo.',
    acao: 'Use "Testar conexão" para ver a causa agora. Se o problema for o token, cole um novo; se a mensagem se repetir por mais de um dia, fale com o suporte.',
    numerosParados: true,
  },
  DISABLED: {
    label: 'Sincronização pausada',
    tone: 'neutral',
    icon: Pause,
    explica:
      'Alguém pausou a sincronização desta conta. Nada foi apagado: os números param onde estavam.',
    acao: 'Reative a conta quando quiser voltar a receber os números do dia.',
    numerosParados: true,
  },
};

/**
 * O status do CARTÃO — que não é só o da coluna.
 *
 * `ACTIVE` quer dizer "está na fila do sincronizador", não "está funcionando". Uma conta que
 * nunca leu nada aparecia "Sincronizando", em verde, enquanto o token nem existia no servidor
 * (Shermam, 05/10). Sem nenhuma leitura bem-sucedida, o verde é afirmação sem medição.
 */
export const AGUARDANDO_PRIMEIRA_LEITURA: StatusConexaoSpec = {
  label: 'Aguardando a 1ª leitura',
  tone: 'neutral',
  icon: Hourglass,
  explica:
    'A conta foi conectada e ainda não houve nenhuma leitura bem-sucedida. Use "Testar conexão" para confirmar agora que o token funciona.',
  acao: null,
  numerosParados: false,
};

export function statusDoCartao(conexao: {
  status: AdsConnectionStatus;
  lastSyncAt: string | null;
}): StatusConexaoSpec {
  if (conexao.status === 'ACTIVE' && conexao.lastSyncAt === null)
    return AGUARDANDO_PRIMEIRA_LEITURA;
  return STATUS_CONEXAO[conexao.status];
}

/**
 * O `account_status` da Meta em palavras. É o estado da CONTA DE ANÚNCIOS lá na Meta —
 * diferente do estado da nossa conexão: o token pode estar perfeito e a conta, desativada
 * por pagamento, e aí não há gasto para ler.
 */
export function statusDaContaMeta(codigo: number | null): { label: string; tone: BadgeTone } {
  switch (codigo) {
    case 1:
      return { label: 'Conta ativa na Meta', tone: 'success' };
    case 2:
      return { label: 'Conta desativada na Meta', tone: 'danger' };
    case 3:
      return { label: 'Pagamento pendente na Meta', tone: 'warning' };
    case 7:
      return { label: 'Em análise de risco na Meta', tone: 'warning' };
    case 8:
      return { label: 'Acerto pendente na Meta', tone: 'warning' };
    case 9:
      return { label: 'Em período de carência na Meta', tone: 'warning' };
    case 100:
      return { label: 'Encerramento pendente na Meta', tone: 'danger' };
    case 101:
      return { label: 'Conta encerrada na Meta', tone: 'danger' };
    case null:
      return { label: 'Status da conta não informado', tone: 'neutral' };
    default:
      return { label: `Status da conta na Meta: ${codigo}`, tone: 'neutral' };
  }
}

/** Ícone do botão que desfaz a conexão — desconectar não é "excluir a conta na Meta". */
export const ICONE_DESCONECTAR = Unplug;

/* ========================================================================== */
/*  A hierarquia                                                               */
/* ========================================================================== */

export interface NivelSpec {
  /** Como se chama uma. */
  label: string;
  /** Como se chama a aba (plural). */
  plural: string;
  /** Uma linha dizendo o que se vê nesse nível. */
  explica: string;
}

/**
 * Conta → campanha → conjunto → anúncio.
 *
 * A CONTA não é um nível: ela é a conexão (`ads_connections`), e por isso aparece como o
 * cartão de cima da tela, não como aba. Os três níveis abaixo são `ads_entities.level`.
 */
export const NIVEL: Record<AdsLevel, NivelSpec> = {
  CAMPAIGN: {
    label: 'Campanha',
    plural: 'Campanhas',
    explica: 'O objetivo que você definiu na Meta. É o recorte mais alto dentro da conta.',
  },
  ADSET: {
    label: 'Conjunto',
    plural: 'Conjuntos',
    explica: 'Dentro da campanha: público, orçamento e posicionamento.',
  },
  AD: {
    label: 'Anúncio',
    plural: 'Anúncios',
    explica: 'O criativo que a pessoa vê. É aqui que dá para comparar peça com peça.',
  },
};

/** A ordem das abas segue a hierarquia — não é ordem alfabética nem de uso. */
export const NIVEIS: readonly AdsLevel[] = ['CAMPAIGN', 'ADSET', 'AD'];

/**
 * O caminho até a entidade, do pai mais alto para o mais baixo (sem ela própria).
 *
 * Um conjunto chamado "Mulheres 25-34" não diz nada sozinho: o que informa é "Black Friday
 * ▸ Mulheres 25-34". Sobe pelo `parentExternalId` porque é o que a Meta devolve, e para
 * quando o pai não está no espelho — conta recém-conectada tem a métrica de um nível antes
 * de ter a árvore inteira, e um caminho pela metade é melhor que um caminho inventado.
 */
export function caminhoDaEntidade(
  externalId: string,
  porExternalId: ReadonlyMap<string, AdsEntity>,
): string[] {
  const caminho: string[] = [];
  let atual = porExternalId.get(externalId);
  /** Trava de ciclo: dado do provider não é promessa, e um pai que aponta para o filho travaria a tela. */
  let passos = 0;

  while (atual?.parentExternalId && passos < NIVEIS.length) {
    const pai = porExternalId.get(atual.parentExternalId);
    if (!pai) break;
    caminho.unshift(pai.name ?? pai.externalId);
    atual = pai;
    passos += 1;
  }

  return caminho;
}

export function indexarEntidades(entidades: readonly AdsEntity[]): Map<string, AdsEntity> {
  return new Map(entidades.map((entidade) => [entidade.externalId, entidade]));
}

/* ========================================================================== */
/*  O período                                                                  */
/* ========================================================================== */

export const PERIODOS_ADS = [
  { id: 'D7', label: 'Últimos 7 dias', dias: 7 },
  { id: 'D14', label: 'Últimos 14 dias', dias: 14 },
  { id: 'D28', label: 'Últimos 28 dias', dias: 28 },
] as const;

export type PeriodoAdsId = (typeof PERIODOS_ADS)[number]['id'];

export function periodoAds(id: PeriodoAdsId): (typeof PERIODOS_ADS)[number] {
  return PERIODOS_ADS.find((periodo) => periodo.id === id) ?? PERIODOS_ADS[0];
}

/**
 * O "hoje" da CONTA DE ANÚNCIO, em `YYYY-MM-DD`.
 *
 * Por que não o fuso do workspace, como em todo o resto do painel: a Insights API fecha o
 * dia no fuso da ad account e não aceita outro (decisão 4 do `schema/ads.ts`). Pedir
 * `since`/`until` no fuso do workspace deslocaria a janela em algumas horas — e o sintoma
 * disso, perto da meia-noite, é "o gasto de ontem some e reaparece".
 *
 * `now` é PARÂMETRO: teste de regra de tempo usa relógio injetado, senão ele passa às 14h e
 * falha às 2h.
 */
export function diaDaConta(now: Date, timeZone: string): string {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  const pega = (tipo: Intl.DateTimeFormatPartTypes): string =>
    partes.find((parte) => parte.type === tipo)?.value ?? '';

  return `${pega('year')}-${pega('month')}-${pega('day')}`;
}

/** `('2026-09-17', 7)` → `{ since: '2026-09-11', until: '2026-09-17' }` (7 dias contando hoje). */
export function janelaDeDias(hoje: string, dias: number): { since: string; until: string } {
  return { since: somarDias(hoje, -(dias - 1)), until: hoje };
}

/** Aritmética de calendário em UTC, sem fuso no meio — mesma de `ads-sync.ts`. */
export function somarDias(dia: string, delta: number): string {
  const base = new Date(`${dia}T00:00:00.000Z`);
  base.setUTCDate(base.getUTCDate() + delta);
  return base.toISOString().slice(0, 10);
}

/* ========================================================================== */
/*  Apresentação (conversão de unidade e rótulo — não é cálculo)               */
/* ========================================================================== */

/**
 * Centavos → a unidade que o `Intl` formata como moeda.
 *
 * É conversão de UNIDADE, não cálculo de métrica: o número já veio decidido do servidor, e
 * o que muda aqui é a casa decimal. `null` continua `null` — dividir "não há o que medir"
 * por cem produziria zero, que é a afirmação oposta (invariante 8).
 */
export function deCentavos(centavos: number | null | undefined): number | null {
  if (centavos === null || centavos === undefined || !Number.isFinite(centavos)) return null;
  return centavos / 100;
}

/** `2026-09-17` → `17/09`. O dia já vem no fuso da conta; aqui é só rótulo. */
export function diaCurto(dia: string): string {
  const [, mes = '', numero = ''] = dia.split('-');
  return `${numero}/${mes}`;
}

/** `2026-09-17` → `17/09/2026`. Vai no `title`, para quem precisa do ano. */
export function diaLongo(dia: string): string {
  const [ano = '', mes = '', numero = ''] = dia.split('-');
  return `${numero}/${mes}/${ano}`;
}

/**
 * Como a conta se chama na tela.
 *
 * O nome vem da Meta na primeira sincronização; antes disso só existe o `act_…`, e é ele
 * que aparece. Inventar "Conta sem nome" esconderia justamente o identificador que a pessoa
 * usa para conferir se conectou a conta certa.
 */
export function nomeDaConta(conexao: { name: string | null; externalAccountId: string }): string {
  return conexao.name ?? conexao.externalAccountId;
}

/* ========================================================================== */
/*  O estado vazio que ensina                                                  */
/* ========================================================================== */

/**
 * **O passo a passo não mora mais aqui.**
 *
 * Ele virou `GUIA_META_ADS`, em `features/guias/conexoes.ts`, junto com os guias do
 * WhatsApp e do redirecionador — e junto com a parte que faltava: a lista de
 * sintoma → causa (`quandoNaoFunciona`). A lista antiga daqui explicava o CAMINHO e não as
 * PEDRAS, e foi exatamente por isso que o dono tropeçou três vezes no mesmo dia com o
 * passo a passo aberto na frente dele.
 *
 * Duas listas do mesmo texto divergem na primeira correção e ninguém descobre qual das
 * duas está na tela — por isso esta foi REMOVIDA, e não deixada "por compatibilidade".
 */

/** O que fazer quando não se tem permissão para conectar. Quem barra é o servidor. */
export const SEM_PERMISSAO_PARA_CONECTAR =
  'Conectar uma conta de anúncios é coisa de administrador do workspace. Peça a um deles — a leitura dos números continua valendo para você.';

/**
 * Por que não existe "sincronizar agora".
 *
 * Dito na tela, e não só no código, porque a primeira pergunta de quem abre o painel às 23h
 * é "cadê o botão de atualizar". A cota da conta do cliente é o recurso escasso desta
 * integração: uma requisição HTTP não pode queimá-la.
 */
export const PORQUE_NAO_TEM_ATUALIZAR =
  'Os números chegam sozinhos, de hora em hora, pelo sincronizador. "Sincronizar agora" pede uma leitura extra desta conta — no máximo uma a cada 5 minutos, porque cada leitura gasta a cota da SUA conta de anúncios na Meta.';
